import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { type DocumentDetailsRelease, type IdAndTitle } from "~/api/types";
import { useQuizzes } from "~/api/v2/useQuizzes";
import { useQuizTakers } from "~/api/v2/useQuizTakers";
import { GraduationCap } from "lucide-react";
import { Button } from "~/atoms/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from "~/atoms/empty";
import { Item, ItemContent, ItemMedia, ItemTitle } from "~/atoms/item";
import { CenterSpinner, Spinner } from "~/atoms/spinner";
import ErrorState from "~/molecules/ErrorState";
import InviteQuizTakersModal from "./InviteQuizTakersModal";
import { CreateQuizForRelease } from "./CreateQuizForRelease";
import { QuizTakersOverview } from "./QuizTakersOverview";

interface Props {
  release: DocumentDetailsRelease;
  category: IdAndTitle | null;
}

/**
 * Quiz management for one document release, shown alongside the readers
 * overview in the anchoring tab. Lets managers create the quiz, open it for
 * editing, invite takers, and follow up on who has completed it.
 */
export const QuizManagementSection = ({ release, category }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: quizzes, isLoading, error, mutate } = useQuizzes(release.id);
  const quiz = quizzes?.[0];
  const isGenerating = quiz?.status === "generating";
  const takers = useQuizTakers(isGenerating ? undefined : quiz?.id);
  const [inviteOpen, setInviteOpen] = useState(false);

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (error) {
    return <ErrorState />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold">
          {t(($) => $.anchoring.manage.heading)}
        </h3>
        {quiz && !isGenerating && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setInviteOpen(true)}>
              {t(($) => $.anchoring.actions.inviteTakers)}
            </Button>
            <Button onClick={() => void navigate(`/anchoring/${quiz.id}`)}>
              {t(($) => $.anchoring.manage.edit)}
            </Button>
          </div>
        )}
      </div>

      {isGenerating ? (
        <Item variant="muted">
          <ItemMedia>
            <Spinner className="size-4" />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>{t(($) => $.anchoring.manage.creating)}</ItemTitle>
          </ItemContent>
        </Item>
      ) : quiz ? (
        <QuizTakersOverview
          report={takers.data}
          isLoading={takers.isLoading}
          error={takers.error}
        />
      ) : (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <GraduationCap />
            </EmptyMedia>
            <EmptyDescription>
              {t(($) => $.anchoring.manage.empty)}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <CreateQuizForRelease
              documentReleaseId={release.id}
              onCreated={() => void mutate()}
            />
          </EmptyContent>
        </Empty>
      )}

      {quiz && inviteOpen && (
        <InviteQuizTakersModal
          quizzes={[{ quizId: quiz.id, category }]}
          onOpenChange={setInviteOpen}
          onInvited={() => {
            void takers.mutate();
            void mutate();
          }}
        />
      )}
    </div>
  );
};
